import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { message, notification, Tooltip } from 'antd';
import { PlusOutlined, FolderAddOutlined, SyncOutlined } from '@ant-design/icons';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectCurrentAdmin } from '@/redux/auth/selectors';
import { usePermission } from '@/customHooks/usePermission';
import { connectSocket, releaseSocket } from '@/services/socketClient';
import useMediaQuery from '@/hooks/useMediaQuery/useMediaQuery';
import ProjectSidebar from '../ProjectManagement/ProjectSidebar';

import ProjectKPICards from './ProjectKPICards';
import ProjectFilters from './ProjectFilters';
import ProjectListTable from './ProjectListTable';
import ProjectDetailsModal from './ProjectDetailsModal';
import CreateProjectModal from '../ProjectManagement/modals/CreateProjectModal';
import NewProjectRequestModal from './NewProjectRequestModal';

import {
    fetchProjects,
    fetchEmployeeNames,
    fetchProjectDashboardStats,
    fetchPendingProjectRequests,
    fetchProjectById,
} from './api/projectDashboardApi';
import { TakeTourButton, ProjectManagementTourProvider, ProjectManagementTour } from '../ProjectManagement/tour';

// Module-level cache to prevent unnecessary re-fetching on route tab navigation
let dashboardCache = {
    userId: null,
    projects: null,
    employees: null,
    stats: null,
    pendingRequests: null,
    timestamp: 0,
};

/**
 * ProjectDashboard
 * Central project directory and project-level overview.
 * Displays KPI cards, search/filters, and paginated project list.
 * Skips header — TopNav is rendered globally by the ERP shell.
 */
export default function ProjectDashboard({
    hideSidebar = false,
    targetProjectId = null,
    onClearTargetProject = null,
}) {
    const history = useHistory();
    const location = useLocation();
    const currentAdmin = useSelector(selectCurrentAdmin);
    const { canCreate: canCreateProject, canApprove: canApproveFromProjects } = usePermission('Projects');
    const { canApprove: canApproveProject } = usePermission('ProjectManagement');
    const userRole = (currentAdmin?.role || '').toLowerCase();

    const isElevated = ['super_admin', 'admin', 'project manager', 'manager'].includes(userRole);
    const hasProjectCreateAccess = isElevated || canCreateProject;
    // Edit access is determined per-project (super_admin always, or project manager and project owner)
    const hasProjectEditAccess = true;
    const hasProjectApproveAccess = isElevated || canApproveProject || canApproveFromProjects;

    // Sidebar breakpoints (mirrors ProjectManagement/index.jsx):
    //   < 768px    → fully hidden (isHidden=true)
    //   768–1199px → collapsed to icon rail
    //   ≥ 1200px   → fully expanded
    const isMobileHidden = useMediaQuery('(max-width: 767px)');
    const isMobile = useMediaQuery('(max-width: 1199px)');
    const [sidebarOpen, setSidebarOpen] = useState(() => (
        typeof window !== 'undefined' ? window.innerWidth >= 1200 : true
    ));

    useEffect(() => {
        setSidebarOpen(!isMobile);
    }, [isMobile]);

    const handleSidebarToggle = () => {
        setSidebarOpen((prev) => !prev);
    };

    const handleNavChange = (navKey) => {
        if (navKey === 'overview') {
            history.push('/project-management');
        } else if (navKey === 'milestones') {
            history.push('/project-management', { activeNav: 'milestones' });
        } else if (navKey === 'goals') {
            history.push('/project-management', { activeNav: 'goals' });
        } else if (navKey === 'reports') {
            history.push('/project-management', { activeNav: 'reports' });
        }
    };

    const isCacheValidForUser = dashboardCache.userId === currentAdmin?._id;

    // ── Data state (initialized from cache if present for instant rendering) ──
    const [projects, setProjects] = useState(() => (isCacheValidForUser ? dashboardCache.projects : null) || []);
    const [employees, setEmployees] = useState(() => (isCacheValidForUser ? dashboardCache.employees : null) || []);
    const [stats, setStats] = useState(() => (isCacheValidForUser ? dashboardCache.stats : null) || {});
    const [loading, setLoading] = useState(() => (isCacheValidForUser ? !dashboardCache.projects : true));
    const [refreshing, setRefreshing] = useState(false);

    // ── Filter state ──────────────────────────────────────────────────────────
    const [filters, setFilters] = useState({
        search: '',
        type: 'All',
        category: null,
        subCategory: null,
        duration: null,
        status: null,
        department: null,
        owner: null,
    });

    // ── Pagination state ──────────────────────────────────────────────────────
    const [pagination, setPagination] = useState({
        page: 1,
        pageSize: 10,
        total: 0,
    });

    // ── Modal state ───────────────────────────────────────────────────────────
    const [isCreateProjectOpen, setIsCreateProjectOpen] = useState(false);
    const [selectedProjectForModal, setSelectedProjectForModal] = useState(null);
    const [isModalEditingInitial, setIsModalEditingInitial] = useState(false);
    const [projectToEdit, setProjectToEdit] = useState(null);
    const [pendingRequests, setPendingRequests] = useState(() => (isCacheValidForUser ? dashboardCache.pendingRequests : null) || []);
    const [selectedRequestForModal, setSelectedRequestForModal] = useState(null);

    const handleReviewRequest = (requestData) => {
        setProjectToEdit(requestData);
        setIsCreateProjectOpen(true);
    };

    // ── Handle incoming deep link navigation (e.g. from Inbox) ─────────────
    useEffect(() => {
        if (!location?.state || !hasProjectApproveAccess) return;
        const { openPendingRequests, requestId, creProjectId, pcrNumber, projectName } = location.state;
        if (openPendingRequests || requestId || creProjectId || pcrNumber) {
            const findMatch = (list) => {
                if (!Array.isArray(list) || list.length === 0) return null;
                return list.find((r) =>
                    (requestId && String(r._id) === String(requestId)) ||
                    (creProjectId && r.creProjectId === creProjectId) ||
                    (pcrNumber && r.pcrNumber === pcrNumber) ||
                    (projectName && r.name && r.name.toLowerCase() === projectName.toLowerCase())
                );
            };

            const matched = findMatch(pendingRequests);
            if (matched) {
                setSelectedRequestForModal(matched);
            } else {
                fetchPendingProjectRequests().then((res) => {
                    const freshList = res?.data?.result || [];
                    setPendingRequests(freshList);
                    const freshMatch = findMatch(freshList);
                    if (freshMatch) {
                        setSelectedRequestForModal(freshMatch);
                    } else if (freshList.length > 0) {
                        setSelectedRequestForModal(freshList[0]);
                    }
                }).catch(() => { });
            }
        }
    }, [location?.state, pendingRequests, hasProjectApproveAccess]);

    // ── Handle incoming direct project navigation (open ProjectDetailsModal) ──
    useEffect(() => {
        const targetId = targetProjectId || location?.state?.projectId || (location?.state?.openProjectModal && location?.state?.project?._id);
        if (!targetId && !location?.state?.project) return;

        // 1. Try to find in already loaded projects first (contains full fields & task stats)
        if (Array.isArray(projects) && projects.length > 0 && targetId) {
            const match = projects.find((p) => String(p._id) === String(targetId));
            if (match) {
                setSelectedProjectForModal(match);
                onClearTargetProject?.();
                return;
            }
        }

        // 2. If a full project object is provided in navigation state (has status/createdAt), open immediately
        if (location?.state?.project && typeof location.state.project === 'object' && location.state.project.name && location.state.project.status) {
            setSelectedProjectForModal(location.state.project);
            onClearTargetProject?.();
            return;
        }

        // 3. Otherwise fetch full project by ID from API
        if (targetId) {
            fetchProjectById(targetId)
                .then((res) => {
                    if (res?.data?.result) {
                        setSelectedProjectForModal(res.data.result);
                        onClearTargetProject?.();
                    }
                })
                .catch((err) => {
                    console.error('Failed to load project details for modal:', targetId, err);
                });
        }
    }, [targetProjectId, location?.state, projects, onClearTargetProject]);

    // ── Data fetching ─────────────────────────────────────────────────────────
    const loadData = useCallback(async (force = false) => {
        const isCacheValid = dashboardCache.userId === currentAdmin?._id;
        // If we already have fresh cached data for this user and not forced, skip hitting the network
        if (!force && isCacheValid && dashboardCache.projects && (Date.now() - dashboardCache.timestamp < 300000)) {
            setProjects(dashboardCache.projects);
            setEmployees(dashboardCache.employees || []);
            setStats(dashboardCache.stats || {});
            setPendingRequests(dashboardCache.pendingRequests || []);
            setLoading(false);
            return;
        }

        if (force) {
            setRefreshing(true);
        } else if (!isCacheValid || !dashboardCache.projects) {
            setLoading(true);
        }

        try {
            const pendingPromise = hasProjectApproveAccess
                ? fetchPendingProjectRequests().catch(() => null)
                : Promise.resolve({ data: { result: [] } });

            const [projectsRes, employeesRes, statsRes, pendingRes] = await Promise.allSettled([
                fetchProjects(),
                fetchEmployeeNames(),
                fetchProjectDashboardStats().catch(() => null), // Graceful fallback if endpoint not ready
                pendingPromise,
            ]);

            let fetchedProjects = [];
            if (projectsRes.status === 'fulfilled') {
                fetchedProjects = projectsRes.value.data?.result || [];
                setProjects(fetchedProjects);
            }

            let fetchedEmployees = [];
            if (employeesRes.status === 'fulfilled') {
                fetchedEmployees = (employeesRes.value.data?.names || []).map((e) => ({
                    _id: e.id,
                    name: e.fullName || `${e.name || ''} ${e.surname || ''}`.trim(),
                    email: e.email || '',
                }));
                setEmployees(fetchedEmployees);
            }

            let fetchedStats = {};
            if (statsRes.status === 'fulfilled' && statsRes.value?.data) {
                fetchedStats = statsRes.value.data.result || {};
                setStats(fetchedStats);
            }

            let fetchedPending = [];
            if (pendingRes.status === 'fulfilled' && pendingRes.value?.data?.result) {
                fetchedPending = pendingRes.value.data.result;
                setPendingRequests(fetchedPending);
            }

            // Sync cache with current user id
            dashboardCache = {
                userId: currentAdmin?._id,
                projects: fetchedProjects,
                employees: fetchedEmployees,
                stats: fetchedStats,
                pendingRequests: fetchedPending,
                timestamp: Date.now(),
            };

            if (force) {
                message.success('Project dashboard refreshed!');
            }
        } catch (err) {
            console.error('[ProjectDashboard] loadData error:', err);
            message.error('Failed to load project data.');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [currentAdmin?._id, hasProjectApproveAccess]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // ── Targeted In-Memory Update (0ms delay, no heavy reload) ─────────────
    const applyGranularProjectUpdate = useCallback((updatePayload) => {
        if (!updatePayload) return;

        const projectId = updatePayload.projectId || updatePayload.relatedData?.projectId;
        const creProjectId = updatePayload.creProjectId || updatePayload.relatedData?.creProjectId;
        const milestoneId = updatePayload.milestoneId || updatePayload.relatedData?.milestoneId;
        const creMilestoneId = updatePayload.creMilestoneId || updatePayload.relatedData?.creMilestoneId;
        const status = updatePayload.status || updatePayload.relatedData?.status;
        const updatedProject = updatePayload.project;

        if (!projectId && !creProjectId && !updatedProject) return;

        console.log('⚡ [Dashboard Live Update] Applying granular in-memory update for project:', projectId || creProjectId);

        setProjects((prevProjects) => {
            return prevProjects.map((p) => {
                const matchId = (projectId && String(p._id) === String(projectId)) ||
                    (creProjectId && p.creProjectId === creProjectId);
                if (!matchId) return p;

                if (updatedProject && updatedProject._id) {
                    return { ...p, ...updatedProject };
                }

                const updatedMilestones = (p.milestones || []).map((m) => {
                    const matchMs = (milestoneId && String(m._id) === String(milestoneId)) ||
                        (creMilestoneId && m.creMilestoneId === creMilestoneId) ||
                        (updatePayload.title && (m.title === updatePayload.title || m.name === updatePayload.title));
                    if (matchMs && status) {
                        return { ...m, status, workStatus: status };
                    }
                    return m;
                });

                return {
                    ...p,
                    milestones: updatedMilestones,
                };
            });
        });

        if (dashboardCache.projects) {
            dashboardCache.projects = dashboardCache.projects.map((p) => {
                const matchId = (projectId && String(p._id) === String(projectId)) ||
                    (creProjectId && p.creProjectId === creProjectId);
                if (!matchId) return p;
                if (updatedProject && updatedProject._id) return { ...p, ...updatedProject };
                return p;
            });
        }

        // Synchronize the currently open View Details modal live
        setSelectedProjectForModal((prevModalProject) => {
            if (!prevModalProject) return null;
            const matchModal = (projectId && String(prevModalProject._id) === String(projectId)) ||
                (creProjectId && prevModalProject.creProjectId === creProjectId);
            if (!matchModal) return prevModalProject;

            if (updatedProject && updatedProject._id) {
                return { ...prevModalProject, ...updatedProject };
            }

            const updatedMilestones = (prevModalProject.milestones || []).map((m) => {
                const matchMs = (milestoneId && String(m._id) === String(milestoneId)) ||
                    (creMilestoneId && m.creMilestoneId === creMilestoneId) ||
                    (updatePayload.title && (m.title === updatePayload.title || m.name === updatePayload.title));
                if (matchMs && status) {
                    return { ...m, status, workStatus: status };
                }
                return m;
            });

            return {
                ...prevModalProject,
                milestones: updatedMilestones,
            };
        });
    }, []);

    // ── Real-time Socket: connect whenever currentAdmin._id is available ────────
    useEffect(() => {
        const userId = currentAdmin?._id?.toString();
        const role = currentAdmin?.role || 'super_admin';
        if (!userId) return;

        let socket;
        try {
            socket = connectSocket({ userId, role });
            if (!socket) return;

            socket.on('project_request:created', (data) => {
                console.log('🔔 [Dashboard] CRE Project Request received:', data);
                new Audio('/livechat-129007.mp3').play().catch(() => { });
                notification.info({
                    message: '🔔 New CRE Project Request Received!',
                    description: `Project "${data.name || 'New Project'}" (${data.pcrNumber || ''}) was submitted by BD and is ready for PM review.`,
                    duration: 8,
                    style: { borderRadius: '12px' },
                });
                loadData();
            });

            socket.on('project_request:updated', () => {
                loadData();
            });

            socket.on('project:created', (data) => {
                console.log('🔔 [Dashboard] ERP Project Created:', data);
                // Skip toast & sound if the current user is the creator
                const isCreator = data.creatorId && userId && String(data.creatorId) === String(userId);
                if (!isCreator) {
                    new Audio('/livechat-129007.mp3').play().catch(() => { });
                    notification.success({
                        message: '🚀 Project Approved & Activated!',
                        description: `Project "${data.project?.name || 'New Project'}" has been activated in ERP and synced with CRE.`,
                        duration: 6,
                        style: { borderRadius: '12px' },
                    });
                }
                loadData();
            });

            socket.on('project:updated', (data) => {
                console.log('⚡ [Dashboard] Realtime project:updated received:', data);
                applyGranularProjectUpdate(data);
            });

            socket.on('new_notification', (data) => {
                const nType = data?.notificationType || '';
                if (nType.includes('milestone') || nType.includes('project') || nType.includes('cre')) {
                    applyGranularProjectUpdate(data);
                }
            });
        } catch (e) {
            console.error('[ProjectDashboard] Socket initialization error:', e);
        }

        return () => {
            if (socket) {
                socket.off('project_request:created');
                socket.off('project_request:updated');
                socket.off('project:created');
                socket.off('project:updated');
                socket.off('new_notification');
            }
            releaseSocket();
        };
    }, [currentAdmin?._id, loadData, applyGranularProjectUpdate]);

    // ── Compute stats from loaded data (fallback if backend endpoint not ready) ──
    const computedStats = useMemo(() => {
        // If backend stats were returned, use them
        if (stats.total !== undefined) return stats;

        // Otherwise, compute from client-side data
        const total = projects.length;
        const clientProjects = projects.filter(p => p.isClientProject || p.projectType === 'Client');
        const inhouseProjects = projects.filter(p => !p.isClientProject && p.projectType !== 'Client');

        const countByStatus = (list) => {
            const result = {};
            list.forEach(p => {
                result[p.status] = (result[p.status] || 0) + 1;
            });
            return result;
        };

        return {
            total,
            clientCount: clientProjects.length,
            inhouseCount: inhouseProjects.length,
            clientByStatus: countByStatus(clientProjects),
            inhouseByStatus: countByStatus(inhouseProjects),
        };
    }, [stats, projects]);

    // ── Departments list (from SignUpEmployee.jsx) ────────────────────────────
    const DEPARTMENTS = useMemo(() => [
        'Marketing',
        'Development',
        'Human Resource',
        'Finance',
        'Customer Support',
        'Business Development',
        'Project Management Office',
    ], []);

    // ── Filter logic ──────────────────────────────────────────────────────────
    const filteredProjects = useMemo(() => {
        let result = [...projects];

        // Type filter
        if (filters.type === 'Client') {
            result = result.filter(p => p.isClientProject || p.projectType === 'Client');
        } else if (filters.type === 'In-house') {
            result = result.filter(p => !p.isClientProject && p.projectType !== 'Client');
        }

        // Category filter
        if (filters.category) {
            result = result.filter(p => (p.category || p.projectType) === filters.category);
        }

        // Sub-Category filter
        if (filters.subCategory) {
            result = result.filter(p => (p.subCategory || p.projectSubType) === filters.subCategory);
        }

        // Duration filter
        if (filters.duration) {
            result = result.filter(p => {
                const mode = p.durationMode || (p.dueDate ? 'Fixed' : 'Ongoing');
                return mode === filters.duration;
            });
        }

        // Status filter
        if (filters.status) {
            result = result.filter(p => {
                if (filters.status === 'Active') {
                    return p.status === 'Active' || p.status === 'In Progress';
                }
                if (filters.status === 'In Progress') {
                    return p.status === 'In Progress' || p.status === 'Active';
                }
                if (filters.status === 'Closed') {
                    return p.status === 'Closed' || p.status === 'Close';
                }
                if (filters.status === 'Not Active') {
                    return p.status === 'Not Active' || p.status === 'Inactive';
                }
                if (filters.status === 'Draft') {
                    return p.isDraft === true || p.status === 'Draft';
                }
                if (filters.status === 'Backlog / Upcoming') {
                    return p.status === 'Backlog' || p.status === 'Backlog / Upcoming' || p.status === 'Upcoming';
                }
                return p.status === filters.status;
            });
        }

        // Department filter (matches owner's or members' department)
        if (filters.department) {
            result = result.filter(p => {
                const ownerDept = p.createdBy?.department || p.department;
                const memberDepts = (p.members || []).map(m => m?.department);
                return ownerDept === filters.department || memberDepts.includes(filters.department);
            });
        }

        // Owner filter
        if (filters.owner) {
            result = result.filter(p =>
                String(p.createdBy?._id || p.createdBy) === String(filters.owner)
            );
        }

        // Search filter (project name, code, client name, owner name, member names)
        if (filters.search.trim()) {
            const query = filters.search.toLowerCase().trim();
            result = result.filter(p => {
                // 1. Resolve Owner Name
                let resolvedOwnerName = '';
                if (p.createdBy?.name) {
                    resolvedOwnerName = `${p.createdBy.name} ${p.createdBy.surname || ''}`.trim();
                } else {
                    const emp = employees.find(e => String(e._id) === String(p.createdBy));
                    if (emp?.name) resolvedOwnerName = emp.name;
                }
                if (!resolvedOwnerName && p.supervisor) {
                    resolvedOwnerName = p.supervisor;
                }
                resolvedOwnerName = resolvedOwnerName.toLowerCase();

                // 2. Project Name
                const projectName = (p.name || '').toLowerCase();

                // 3. Client Name
                const clientName = (p.clientName || '').toLowerCase();

                // 4. Project Code
                const originalIndex = projects.findIndex(proj => proj._id === p._id);
                const projectCode = (p.projectCode || `PRJ-${String(originalIndex + 1).padStart(3, '0')}`).toLowerCase();

                // 5. Project Type
                const projectType = p.isClientProject ? 'client' : 'in-house';

                // 6. Member Names
                const memberNames = (p.members || []).map(m =>
                    (m?.name || employees.find(e => String(e._id) === String(m?._id || m))?.name || '').toLowerCase()
                ).join(' ');

                return (
                    projectName.includes(query) ||
                    clientName.includes(query) ||
                    resolvedOwnerName.includes(query) ||
                    projectCode.includes(query) ||
                    projectType.includes(query) ||
                    memberNames.includes(query)
                );
            });
        }

        return result;
    }, [projects, filters, employees]);

    // ── Paginated slice ───────────────────────────────────────────────────────
    const paginatedProjects = useMemo(() => {
        const start = (pagination.page - 1) * pagination.pageSize;
        return filteredProjects.slice(start, start + pagination.pageSize);
    }, [filteredProjects, pagination.page, pagination.pageSize]);

    // ── Keep pagination total in sync ─────────────────────────────────────────
    useEffect(() => {
        setPagination(prev => ({
            ...prev,
            total: filteredProjects.length,
            page: 1, // reset to page 1 when filters change
        }));
    }, [filteredProjects.length, filters]);

    // ── Handlers ──────────────────────────────────────────────────────────────
    const handleFilterChange = (newFilters) => {
        setFilters(newFilters);
    };

    const handleQuickFilter = ({ type, status }) => {
        setFilters(prev => ({
            ...prev,
            type: type || prev.type,
            status: status || null,
        }));
    };

    const handlePageChange = (page) => {
        setPagination(prev => ({ ...prev, page }));
    };

    const handlePageSizeChange = (pageSize) => {
        setPagination(prev => ({ ...prev, pageSize, page: 1 }));
    };

    const handleProjectCreated = (newProject) => {
        setIsCreateProjectOpen(false);
        setProjectToEdit(null);
        setSelectedRequestForModal(null);

        // Trigger loadData in background without blocking
        loadData().then(async () => {
            // If details modal is open, update it with the fresh project data so activity logs refresh
            if (selectedProjectForModal) {
                try {
                    const res = await fetchProjectById(selectedProjectForModal._id);
                    if (res.data?.result) {
                        setSelectedProjectForModal(res.data.result);
                    }
                } catch (e) { /* silent */ }
            }
        });
    };

    const handleViewDetails = (project) => {
        setIsModalEditingInitial(false);
        setSelectedProjectForModal(project);
    };

    const handleEditProject = (project) => {
        setSelectedProjectForModal(project);
        setIsModalEditingInitial(true);
    };

    // Extract unique departments from employees (placeholder — could come from API)
    const departments = useMemo(() => {
        // For now return empty — could be fetched from backend
        return [];
    }, []);

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <ProjectManagementTourProvider>
            <div className={`min-h-[calc(100vh-56px)] ${hideSidebar ? '' : 'bg-[#f8f9fa] dark:bg-[#141414]'}`}>
                {!hideSidebar && (
                    <ProjectSidebar
                        isOpen={sidebarOpen}
                        isHidden={isMobileHidden}
                        onToggle={handleSidebarToggle}
                        activeNav="dashboard"
                        onNavChange={handleNavChange}
                    />
                )}
                <main
                    className={`max-w-[1920px] mx-auto space-y-4 transition-all duration-300 ease-in-out ${hideSidebar ? '' : 'px-6 py-8'}`}
                    style={{
                        marginLeft: hideSidebar
                            ? '0px'
                            : isMobileHidden
                                ? '0px'
                                : sidebarOpen ? '256px' : '56px',
                    }}
                >

                    {/* Header */}
                    <section className="space-y-6">
                        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                            <div>
                                <h1
                                    className="text-3xl font-bold text-[#003263] dark:text-[#a7c8ff] tracking-tight"
                                    style={{ fontFamily: 'Manrope, sans-serif' }}
                                >
                                    Project Dashboard
                                </h1>
                                <p className="text-slate-500 dark:text-slate-400 font-medium">
                                    Enterprise Resource Planning &amp; Performance Tracking
                                </p>
                            </div>
                            <div className="flex items-center gap-3 flex-wrap">
                                {hasProjectApproveAccess && pendingRequests.length > 0 && (
                                    <button
                                        data-tour="pending-cre-requests-btn"
                                        onClick={() => setSelectedRequestForModal(pendingRequests[0])}
                                        className="relative text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/60 border border-amber-300 dark:border-amber-700 flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm shadow-sm hover:bg-amber-100 dark:hover:bg-amber-900 active:scale-95 transition-all"
                                    >
                                        <span className="w-2.5 h-2.5 rounded-full bg-amber-500 animate-ping" />
                                        Pending CRE Requests ({pendingRequests.length})
                                    </button>
                                )}
                                <Tooltip title="Refresh Projects">
                                    <button
                                        onClick={() => loadData(true)}
                                        disabled={refreshing || loading}
                                        aria-label="Refresh Projects"
                                        className="w-10 h-10 flex items-center justify-center rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-[#1f1f1f] text-slate-600 dark:text-slate-300 hover:text-[#03488a] dark:hover:text-[#a7c8ff] hover:border-[#03488a] dark:hover:border-[#a7c8ff] transition-all shadow-sm active:scale-95 disabled:opacity-50"
                                    >
                                        <SyncOutlined spin={refreshing} style={{ fontSize: 14 }} />
                                    </button>
                                </Tooltip>
                                <TakeTourButton currentNav="dashboard" onNavigate={handleNavChange} />
                                {hasProjectCreateAccess && (
                                    <button
                                        data-tour="new-project-btn"
                                        onClick={() => { setProjectToEdit(null); setIsCreateProjectOpen(true); }}
                                        className="text-white flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-sm shadow-sm hover:opacity-90 active:scale-95 transition-all cursor-pointer"
                                        style={{
                                            background: 'linear-gradient(90deg, #03488A 0%, #00AAF1 100%)',
                                            fontFamily: 'Manrope, sans-serif',
                                        }}
                                    >
                                        <PlusOutlined style={{ fontSize: 12 }} /> Create Project
                                    </button>
                                )}
                            </div>
                        </div>
                    </section>

                    {/* KPI Cards */}
                    <div data-tour="project-kpi-cards">
                        <ProjectKPICards stats={computedStats} onQuickFilter={handleQuickFilter} />
                    </div>

                    {/* Filters */}
                    <div data-tour="project-filters">
                        <ProjectFilters
                            filters={filters}
                            onFilterChange={handleFilterChange}
                            employees={employees}
                            departments={DEPARTMENTS}
                        />
                    </div>

                    {/* Project List Table */}
                    <div data-tour="project-list-table">
                        <ProjectListTable
                            projects={paginatedProjects}
                            employees={employees}
                            loading={loading}
                            pagination={pagination}
                            onPageChange={handlePageChange}
                            onPageSizeChange={handlePageSizeChange}
                            onViewDetails={handleViewDetails}
                            onEditProject={hasProjectEditAccess ? handleEditProject : null}
                            currentAdmin={currentAdmin}
                        />
                    </div>
                </main>

                {/* Create / Edit Project Modal */}
                <CreateProjectModal
                    open={isCreateProjectOpen}
                    projectToEdit={projectToEdit}
                    onClose={() => { setIsCreateProjectOpen(false); setProjectToEdit(null); }}
                    onSuccess={handleProjectCreated}
                />

                {/* Project Details Modal */}
                <ProjectDetailsModal
                    open={Boolean(selectedProjectForModal)}
                    project={selectedProjectForModal}
                    employees={employees}
                    currentAdmin={currentAdmin}
                    initialEditMode={isModalEditingInitial}
                    onClose={() => {
                        setSelectedProjectForModal(null);
                        setIsModalEditingInitial(false);
                    }}
                    onEditProject={handleEditProject}
                    onRefresh={() => loadData(true)}
                    onOpenProject={(proj) => {
                        setSelectedProjectForModal(null);
                        setIsModalEditingInitial(false);
                        history.push('/project-management', { projectId: proj._id });
                    }}
                />

                {/* New Project Request Modal (CRE Intake Preview & Carousel) */}
                <NewProjectRequestModal
                    open={Boolean(selectedRequestForModal)}
                    request={selectedRequestForModal}
                    requests={pendingRequests}
                    onClose={() => setSelectedRequestForModal(null)}
                    onReviewRequest={handleReviewRequest}
                />

                <ProjectManagementTour />
            </div>
        </ProjectManagementTourProvider>
    );
}
